import React from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Chip,
    Button,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Stack,
    Fab,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { Add, Edit, Delete, MoreVert, Archive, Tune } from '@mui/icons-material';
import SpaceDropZone from './SpaceDropZone';
import { isAddedToday } from '../../utils/dateUtils';
import { Plant, Space, Seed, FirestoreTimestamp } from '../../types/shared';
import { useUpload } from '../../contexts/UploadContext';
import { useTranslation } from 'react-i18next';

interface SeasonalityChartProps {
    seed: Seed;
}

const SeasonalityChart = React.memo<SeasonalityChartProps>(({ seed }) => (
    <Box sx={{ position: 'relative', width: '100%', py: 0.5 }}>
        {/* Month Labels */}
        <Box sx={{ display: 'flex', mb: 0.25 }}>
            {['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'].map((m, i) => (
                <Typography key={i} variant="caption" sx={{ flex: 1, textAlign: 'center', color: 'text.secondary', fontSize: '0.55rem', lineHeight: 1 }}>{m}</Typography>
            ))}
        </Box>

        {/* Bars Container */}
        <Box sx={{ display: 'flex', height: 18, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
            {/* Current Date Indicator */}
            {(() => {
                const now = new Date();
                const currentMonth = now.getMonth();
                const currentDay = now.getDate();
                const daysInMonth = new Date(now.getFullYear(), currentMonth + 1, 0).getDate();
                // Calculate position based on equal-width visual months (1/12th each)
                // We use (currentDay - 0.5) to position marker in the middle of the current day slot
                const percent = ((currentMonth + ((currentDay - 0.5) / daysInMonth)) / 12) * 100;
                return (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${percent}%`,
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            bgcolor: 'error.main',
                            zIndex: 2,
                            boxShadow: '0 0 2px rgba(0,0,0,0.5)'
                        }}
                    />
                );
            })()}

            {Array.from({ length: 12 }).map((_, i) => {
                const monthNum = i + 1;
                const indoor = seed.plantIndoor?.[monthNum];
                const outdoor = seed.plantOutdoors?.[monthNum];
                const harvest = seed.harvest?.[monthNum];

                return (
                    <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: i < 11 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                        <Box sx={{ flex: 1, bgcolor: indoor ? 'var(--color-season-indoor)' : 'transparent', opacity: 0.8 }} title="Indoor Planting" />
                        <Box sx={{ flex: 1, bgcolor: outdoor ? 'var(--color-season-outdoor)' : 'transparent', opacity: 0.8 }} title="Outdoor Planting" />
                        <Box sx={{ flex: 1, bgcolor: harvest ? 'var(--color-season-harvest)' : 'transparent', opacity: 0.8 }} title="Harvest" />
                    </Box>
                );
            })}
        </Box>
    </Box>
));

interface GardenOverviewProps {
    view: 'garden' | 'seeds';
    plants: Plant[];
    seeds: Seed[];
    spaces: Space[];
    onEditPlant?: (plant: Plant) => void;
    onRemovePlant?: (plant: Plant) => void;
    onAddPlantToSpace?: (spaceId: string) => void;
    onManageSpaces?: () => void;
    onAddSeed?: () => void;
    onShowSuggestions?: () => void;
    onEditSeed: (seed: Seed) => void;
    onPlantSeed: (seed: Seed, stage: 'Seedling' | 'Plant') => void;
    onDeleteSeed: (seed: Seed) => void;
    onArchiveSeed?: (seed: Seed) => void;

    onPlantMove?: (plantId: string, spaceId: string | null) => void;
    onPlantReorder?: (spaceId: string | null, oldIndex: number, newIndex: number) => void;
    onTransplant?: (plant: Plant) => void;
    onImageClick?: (imageUrl: string) => void;
}

const GardenOverview = React.memo<GardenOverviewProps>(({
    view,
    plants,
    seeds,
    spaces,
    onEditPlant,
    onRemovePlant,

    onAddPlantToSpace,
    onManageSpaces,
    onAddSeed,
    onShowSuggestions,
    onEditSeed,
    onPlantSeed,
    onDeleteSeed,
    onArchiveSeed,

    onPlantMove,
    onPlantReorder,
    onTransplant,
    onImageClick,
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSeedsView = view === 'seeds';
    const { isUploading } = useUpload();

    // DnD sensors for mouse, touch, and keyboard
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end for both spaces and plants
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) return;

        const activeData = active.data.current;
        const overData = over.data.current;

        // Case 1: Plant interactions
        if (activeData?.type === 'plant') {
            let targetSpaceId: string | null = null;

            // Dropped on a space drop zone
            if (overData?.type === 'space-drop') {
                targetSpaceId = overData.spaceId;
            }
            // Dropped on another plant
            else if (overData?.type === 'plant') {
                targetSpaceId = overData.spaceId;

                // If same space, handle reordering within space
                const currentSpaceId = activeData.plant.spaceId || null;
                if (targetSpaceId === currentSpaceId && active.id !== over.id) {
                    // Get plants in this space sorted by order
                    const spacePlants = plants
                        .filter(p => (p.spaceId || null) === currentSpaceId)
                        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

                    const oldIndex = spacePlants.findIndex(p => p.id === active.id);
                    const newIndex = spacePlants.findIndex(p => p.id === over.id);

                    if (oldIndex !== -1 && newIndex !== -1) {
                        onPlantReorder?.(currentSpaceId, oldIndex, newIndex);
                    }
                    return;
                }
            }
            // Dropped on a space header
            else if (overData?.type === 'space') {
                targetSpaceId = over.id === 'unassigned' ? null : over.id as string;
            }

            const currentSpaceId = activeData.plant.spaceId || null;

            if (targetSpaceId !== currentSpaceId) {
                onPlantMove?.(activeData.plant.id, targetSpaceId);
            }
        }
    };

    // Sort spaces by order field
    const sortedSpaces = [...spaces].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const spaceIds = sortedSpaces.map(s => s.id);

    // Group plants by space and sort by order
    const plantsBySpace: Record<string, Plant[]> = {};
    sortedSpaces.forEach(sp => {
        plantsBySpace[sp.id] = plants
            .filter(p => p.spaceId === sp.id)
            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    });
    const unassignedPlants = plants
        .filter(p => !p.spaceId || !spaces.find(s => s.id === p.spaceId))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

    const checkIsExpired = (bestBefore: FirestoreTimestamp | Date | string | undefined) => {
        if (!bestBefore) return false;
        const date = (bestBefore as any).toDate ? (bestBefore as any).toDate() : new Date(bestBefore as string | Date);
        return date < new Date();
    }

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [menuSeed, setMenuSeed] = React.useState<Seed | null>(null);
    const [gardenMenuAnchor, setGardenMenuAnchor] = React.useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, seed: Seed) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuSeed(seed);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSeed(null);
    };

    const handleMenuAction = (action: 'edit' | 'archive' | 'delete') => {
        if (!menuSeed) return;

        if (action === 'edit') {
            onEditSeed(menuSeed);
        } else if (action === 'archive') {
            onArchiveSeed?.(menuSeed);
        } else if (action === 'delete') {
            onDeleteSeed(menuSeed);
        }
        handleMenuClose();
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isSeedsView && (
                        <>
                            <Tooltip title={t('tasks.space_options_btn')}>
                                <IconButton
                                    onClick={(e) => setGardenMenuAnchor(e.currentTarget)}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    <MoreVert />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </Box>

            {/* Garden view menu */}
            <Menu
                anchorEl={gardenMenuAnchor}
                open={Boolean(gardenMenuAnchor)}
                onClose={() => setGardenMenuAnchor(null)}
            >
                <MenuItem onClick={() => { onManageSpaces?.(); setGardenMenuAnchor(null); }}>
                    <ListItemIcon>
                        <Tune fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('tasks.manage_spaces_btn')}</ListItemText>
                </MenuItem>
            </Menu>

            {isSeedsView ? (
                <>
                    {seeds.length > 0 ? (
                        isMobile ? (
                            <Stack spacing={2}>
                                {seeds.map((seed) => {
                                    const currentMonth = new Date().getMonth() + 1;
                                    const isIndoor = seed.plantIndoor && seed.plantIndoor[currentMonth];
                                    const isOutdoor = seed.plantOutdoors && seed.plantOutdoors[currentMonth];
                                    const isExpired = checkIsExpired(seed.bestBefore);

                                    return (
                                        <Card key={seed.id} elevation={0} sx={{
                                            borderRadius: 3,
                                            bgcolor: 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(5px)',
                                            display: 'flex',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }}>
                                            {seed.imageUrl ? (
                                                <Box
                                                    component="img"
                                                    src={seed.imageUrl}
                                                    alt={seed.name}
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        objectFit: 'cover',
                                                        flexShrink: 0,
                                                        alignSelf: 'center',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => onImageClick?.(seed.imageUrl!)}
                                                />
                                            ) : (
                                                <Box
                                                    component="img"
                                                    src="/icons/generic-seedling.webp"
                                                    alt="Seedling"
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        objectFit: 'contain',
                                                        flexShrink: 0,
                                                        alignSelf: 'center',
                                                        bgcolor: 'white',
                                                        borderRadius: 1
                                                    }}
                                                />
                                            )}
                                            <CardContent sx={{ pb: '16px !important', flex: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" component="h3" className="serif-text" sx={{ fontWeight: 600, textAlign: 'left' }}>
                                                            {seed.name}
                                                            {seed.variety && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: -0.5, textAlign: 'left' }}>
                                                                    {seed.variety}
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                        {seed.count !== undefined && seed.count !== null && (
                                                            <Chip label={`×${seed.count}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', ml: 0.5 }} />
                                                        )}
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                            {isUploading(seed.id) && (
                                                                <Chip label={t('tasks.uploading')} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                            )}
                                                            {seed.processed === false && (
                                                                <Chip label={t('tasks.processing')} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255, 152, 0, 0.1)' }} />
                                                            )}
                                                            {isExpired && seed.processed !== false && (
                                                                <Chip label={t('tasks.expired')} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                            )}

                                                            {isAddedToday(seed.createdAt) && (
                                                                <Chip label={t('tasks.new')} size="small" color="primary" variant="filled" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                            )}
                                                            {seed.lastPlantedAt && (
                                                                <Chip
                                                                    label={t('seeds.last_planted', { date: new Date((seed.lastPlantedAt as any).toDate ? (seed.lastPlantedAt as any).toDate() : seed.lastPlantedAt).toLocaleDateString(i18n.language) })}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.65rem', borderColor: 'text.secondary', color: 'text.secondary' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => onPlantSeed(seed, 'Seedling')}
                                                            sx={{
                                                                textTransform: 'none',
                                                                borderRadius: 2,
                                                                bgcolor: isIndoor ? 'primary.main' : 'grey.300',
                                                                color: isIndoor ? 'white' : 'text.primary',
                                                                '&:hover': {
                                                                    bgcolor: isIndoor ? 'primary.dark' : 'grey.400'
                                                                },
                                                                minWidth: 'auto',
                                                                px: 1,
                                                                py: 0.5,
                                                                mr: 0.5
                                                            }}
                                                        >
                                                            {t('tasks.seedling_btn')}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => onPlantSeed(seed, 'Plant')}
                                                            sx={{
                                                                textTransform: 'none',
                                                                borderRadius: 2,
                                                                bgcolor: isOutdoor ? 'primary.main' : 'grey.300',
                                                                color: isOutdoor ? 'white' : 'text.primary',
                                                                '&:hover': {
                                                                    bgcolor: isOutdoor ? 'primary.dark' : 'grey.400'
                                                                },
                                                                minWidth: 'auto',
                                                                px: 1,
                                                                py: 0.5
                                                            }}
                                                        >
                                                            {t('tasks.plant_btn')}
                                                        </Button>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleMenuOpen(e, seed)}
                                                            sx={{ color: 'text.secondary' }}
                                                        >
                                                            <MoreVert fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>

                                                <Box>
                                                    <SeasonalityChart seed={seed} />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        ) : (
                            <TableContainer component={Paper} elevation={0} sx={{
                                borderRadius: 3,
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(5px)',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                            }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                                        <TableRow>
                                            <TableCell sx={{ width: 56, p: 0 }}></TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{t('tasks.table_name')}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{t('tasks.table_planting_months')}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('tasks.table_actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {seeds.map((seed) => {
                                            const currentMonth = new Date().getMonth() + 1; // 1-12
                                            const isIndoor = seed.plantIndoor && seed.plantIndoor[currentMonth];
                                            const isOutdoor = seed.plantOutdoors && seed.plantOutdoors[currentMonth];
                                            const isExpired = checkIsExpired(seed.bestBefore);

                                            return (
                                                <TableRow key={seed.id} sx={{ bgcolor: 'inherit' }}>
                                                    <TableCell sx={{ p: 0, width: 56 }}>
                                                        {seed.imageUrl ? (
                                                            <Box
                                                                component="img"
                                                                src={seed.imageUrl}
                                                                alt={seed.name}
                                                                sx={{ width: 56, height: 56, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                                                                onClick={() => onImageClick?.(seed.imageUrl!)}
                                                            />
                                                        ) : (
                                                            <Box
                                                                component="img"
                                                                src="/icons/generic-seedling.webp"
                                                                alt="Seedling"
                                                                sx={{ width: 56, height: 56, objectFit: 'contain', display: 'block', bgcolor: 'white', borderRadius: 1 }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" component="h3" className="serif-text" sx={{ fontWeight: 600 }}>
                                                                {seed.name}{seed.variety ? ` (${seed.variety})` : ''}
                                                                {seed.count !== undefined && seed.count !== null && (
                                                                    <Chip label={`×${seed.count}`} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5, verticalAlign: 'middle' }} />
                                                                )}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                                {isUploading(seed.id) && (
                                                                    <Chip label={t('tasks.uploading')} size="small" color="info" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                                                                )}
                                                                {seed.processed === false && (
                                                                    <Chip label={t('tasks.processing')} size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                                                                )}
                                                                {isExpired && seed.processed !== false && (
                                                                    <Chip label={t('tasks.expired')} size="small" color="error" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                                                                )}

                                                                {isAddedToday(seed.createdAt) && (
                                                                    <Chip label={t('tasks.new')} size="small" color="primary" variant="filled" sx={{ height: 16, fontSize: '0.6rem' }} />
                                                                )}
                                                                {seed.lastPlantedAt && (
                                                                    <Chip
                                                                        label={t('seeds.last_planted', { date: new Date((seed.lastPlantedAt as any).toDate ? (seed.lastPlantedAt as any).toDate() : seed.lastPlantedAt).toLocaleDateString(i18n.language) })}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ height: 16, fontSize: '0.6rem', borderColor: 'text.secondary', color: 'text.secondary' }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ width: 300 }}>
                                                            <SeasonalityChart seed={seed} />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => onPlantSeed(seed, 'Seedling')}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    borderRadius: 2,
                                                                    bgcolor: isIndoor ? 'primary.main' : 'grey.300',
                                                                    color: isIndoor ? 'white' : 'text.primary',
                                                                    '&:hover': {
                                                                        bgcolor: isIndoor ? 'primary.dark' : 'grey.400'
                                                                    },
                                                                    mr: 1
                                                                }}
                                                            >
                                                                {t('tasks.seedling_btn')}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => onPlantSeed(seed, 'Plant')}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    borderRadius: 2,
                                                                    bgcolor: isOutdoor ? 'primary.main' : 'grey.300',
                                                                    color: isOutdoor ? 'white' : 'text.primary',
                                                                    '&:hover': {
                                                                        bgcolor: isOutdoor ? 'primary.dark' : 'grey.400'
                                                                    }
                                                                }}
                                                            >
                                                                {t('tasks.plant_btn')}
                                                            </Button>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => handleMenuOpen(e, seed)}
                                                                sx={{ color: 'text.secondary' }}
                                                            >
                                                                <MoreVert fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.5)', borderRadius: 4 }}>
                            <Typography color="text.secondary">{t('tasks.no_seeds_library')}</Typography>
                        </Box>
                    )
                    }
                </>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={spaceIds} strategy={verticalListSortingStrategy}>
                        {sortedSpaces.map(space => (
                            <SpaceDropZone
                                key={space.id}
                                space={space}
                                plants={plantsBySpace[space.id] || []}
                                onEditPlant={onEditPlant || (() => { })}
                                onRemovePlant={onRemovePlant}
                                onAddPlantToSpace={onAddPlantToSpace}
                                onTransplant={onTransplant}
                                onImageClick={onImageClick}
                            />
                        ))}
                    </SortableContext>

                    {/* Unassigned plants section */}
                    {(unassignedPlants.length > 0 || spaces.length === 0) && (
                        <SpaceDropZone
                            space={{ id: 'unassigned', name: t('tasks.unassigned_plants'), userId: '' }}
                            plants={unassignedPlants}
                            onEditPlant={onEditPlant || (() => { })}
                            onRemovePlant={onRemovePlant}
                            onAddPlantToSpace={onAddPlantToSpace}
                            isUnassigned={true}
                            onTransplant={onTransplant}
                            onImageClick={onImageClick}
                        />
                    )}

                    {spaces.length === 0 && plants.length === 0 && (
                        <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.5)', borderRadius: 4, mt: 2 }}>
                            <Typography color="text.secondary">{t('tasks.no_spaces_plants')}</Typography>
                        </Box>
                    )}
                </DndContext>
            )}

            {isSeedsView && (
                <Fab
                    color="primary"
                    aria-label="add seed"
                    onClick={onAddSeed}
                    sx={{
                        position: 'fixed',
                        bottom: 100,
                        right: 16,
                        zIndex: 1000
                    }}
                >
                    <Add />
                </Fab>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleMenuAction('edit')}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('tasks.edit_btn')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleMenuAction('archive')}>
                    <ListItemIcon>
                        <Archive fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('tasks.archive_btn')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleMenuAction('delete')} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <Delete fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>{t('tasks.delete_btn')}</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
});

export default GardenOverview;
